import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../db';

import { errorMessage } from '../utils/errors';
import { syncvalue } from '../utils/datetime';
import { syncRecordsets, handleRemoteDeletions } from '../utils/sync';

const template = () => {
  const record = {
    id: null,
    facility_id: null,
    name: '',
    description: '',
    default_assessment_title: '',
    preferred_timeframe_index: '',
    species: [],
    section_a_observations_total: null,
    section_b_observations_total: null,
    section_a_timer_seconds: null,
    section_b_timer_seconds: null,
    allow_single_open_assessment_per_animal: false,
  };

  return { ...record };
};

const transformFromDb = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.default_assessment_title = temp.default_assessment_title || '';
  temp.preferred_timeframe_index = temp.preferred_timeframe_index || '';
  temp.species = Array.isArray(temp.species) ? temp.species : [];
  return temp;
};

const transformToDb = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.default_assessment_title = temp.default_assessment_title || '';
  temp.preferred_timeframe_index = temp.preferred_timeframe_index || null;
  temp.species = Array.isArray(temp.species) ? temp.species : [];
  return { assessment_type: temp };
};

const toDexie = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.default_assessment_title = temp.default_assessment_title || '';
  temp.preferred_timeframe_index = temp.preferred_timeframe_index || '';
  temp.species = Array.isArray(temp.species) ? temp.species : [];
  return temp;
};

const fromDexie = (inputRecord) => {
  const temp = { ...inputRecord };
  temp.default_assessment_title = temp.default_assessment_title || '';
  temp.preferred_timeframe_index = temp.preferred_timeframe_index || '';
  temp.species = Array.isArray(temp.species) ? temp.species : [];
  return temp;
};

const newAssessmentType = () => {
  return template();
};

const listAssessmentTypes = async () => {
  try {
    const temp = await db().assessment_types.toCollection().toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const listAssessmentTypesBySync = async (lastSync) => {
  try {
    const temp = await db().assessment_types.where('sync').aboveOrEqual(lastSync).toArray();
    return temp.map((cur) => fromDexie(cur));
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getAssessmentType = async (id) => {
  try {
    const temp = await db().assessment_types.where('id').equals(id).first();
    return temp ? fromDexie(temp) : undefined;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchAssessmentTypes = async () => {
  try {
    const results = await supabase.from('assessment_types').select('*');
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const fetchAssessmentTypesBySync = async (lastSync) => {
  try {
    const results = await supabase.from('assessment_types').select('*').gte('sync', lastSync);
    if (results.error) throw new Error(`Error ${results.error.code}: ${results.error.message}`);
    if (results.data !== null) {
      return results.data.map((cur) => transformFromDb(cur));
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeAssessmentTypeToDexie = async (inputRecord) => {
  try {
    const cleanRecord = toDexie(inputRecord);
    const id = await db().assessment_types.put(cleanRecord);
    return id;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const writeAssessmentTypeToSupabase = async (inputRecord, facilityId) => {
  try {
    inputRecord.facility_id = inputRecord.facility_id || facilityId;
    const { assessment_type } = transformToDb(inputRecord);

    const results = await supabase.from('assessment_types').upsert(assessment_type);
    if (results.error) throw new Error(errorMessage(results.error));
    return { results, id: assessment_type.id };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const saveAssessmentType = async (inputRecord, facilityId, isOnline) => {
  const syncValue = syncvalue();

  inputRecord.id = inputRecord.id || uuidv4();
  inputRecord.facility_id = inputRecord.facility_id || facilityId;
  inputRecord.sync = syncValue;

  const dexieResults = await writeAssessmentTypeToDexie(inputRecord);
  const supabaseResults = isOnline ? await writeAssessmentTypeToSupabase(inputRecord, facilityId) : { status: 'offline' };
  return { dexieResults, supabaseResults, id: inputRecord.id };
};

const sync = async ({ facilityId, lastSync = null }) => {
  const deletedRecords = await handleRemoteDeletions({
    tableName: 'assessment_types',
    listFunc: listAssessmentTypes,
    supabase,
    dbTable: db().assessment_types,
  });
  console.log('deletedRecords', deletedRecords);

  const { remoteData, localData } = lastSync ? await syncPartialRecordSets(lastSync) : await syncFullRecordSets();
  const results = await syncRecordsets({
    remoteData,
    localData,
    remoteWriteFunc: writeAssessmentTypeToSupabase,
    localWriteFunc: writeAssessmentTypeToDexie,
    remoteWriteFuncArgs: [facilityId],
  });
  return results;
};

const syncFullRecordSets = async () => {
  const remoteData = (await fetchAssessmentTypes()) || [];
  const localData = (await listAssessmentTypes()) || [];

  return { remoteData, localData };
};

const syncPartialRecordSets = async (lastSync) => {
  const remoteData = (await fetchAssessmentTypesBySync(lastSync)) || [];
  const localData = (await listAssessmentTypesBySync(lastSync)) || [];

  return { remoteData, localData };
};

const truncateDexie = async () => {
  return await db().assessment_types.clear();
};

export {
  newAssessmentType,
  listAssessmentTypes,
  getAssessmentType,
  fetchAssessmentTypes,
  saveAssessmentType,
  sync,
  truncateDexie,
};
